import { useEffect, useState } from "react";
import { X, Check, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UNITS, CATEGORIES } from "../lib";

/**
 * Create / edit a part.  Basic fields on top; a "More Details" collapse
 * for the less-common ones (vehicle applicability, HSN, alternate part number,
 * preferred vendor, image, notes) — matching Section 5 of the spec.
 */
export default function PartFormDialog({ open, onClose, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(defaultForm(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(defaultForm(initial));
      setError(null);
      setMoreOpen(false);
    }
  }, [open, initial]);

  useEffect(() => {
    if (open) api.get("/spares/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  }, [open]);

  if (!open) return null;

  const setF = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!form.name.trim()) { setError("Part name is required"); return; }
    setSaving(true); setError(null);
    try {
      const payload = { ...form,
        purchase_price: Number(form.purchase_price) || 0,
        selling_price: Number(form.selling_price) || 0,
        gst_rate: Number(form.gst_rate) || 0,
        reorder_level: Number(form.reorder_level) || 0,
        max_stock_level: form.max_stock_level ? Number(form.max_stock_level) : null,
      };
      if (isEdit) {
        await api.patch(`/spares/parts/${initial.id}`, payload);
        toast.success("Part updated");
      } else {
        await api.post("/spares/parts", payload);
        toast.success("Part created");
      }
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save part");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="spare-part-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
           data-testid="spare-part-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#F7F9FC]">
          <div>
            <h2 className="text-[16px] font-bold text-[#1d3557]">
              {isEdit ? "Edit Part" : "Add Part"}
            </h2>
            <p className="text-[11px] text-[#667085]">Basic info first, everything else in More Details.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-[#E4E7EC] grid place-items-center text-[#667085]"
                  data-testid="spare-part-dialog-close">
            <X size={17} />
          </button>
        </header>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="spare-part-dialog-error">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Part name *">
              <input type="text" value={form.name} onChange={(e) => setF("name", e.target.value)}
                     data-testid="spare-part-name"
                     placeholder="e.g. Air Filter (Swift)"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full focus:border-[#457b9d] outline-none" />
            </Field>
            <Field label="Part number">
              <input type="text" value={form.part_number} onChange={(e) => setF("part_number", e.target.value)}
                     data-testid="spare-part-number"
                     placeholder="OEM or catalog code"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full focus:border-[#457b9d] outline-none font-mono" />
            </Field>
            <Field label="Brand">
              <input type="text" value={form.brand} onChange={(e) => setF("brand", e.target.value)}
                     data-testid="spare-part-brand"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </Field>
            <Field label="Category">
              <select value={form.category} onChange={(e) => setF("category", e.target.value)}
                      data-testid="spare-part-category"
                      className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                <option value="">Select…</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Unit">
              <select value={form.unit} onChange={(e) => setF("unit", e.target.value)}
                      data-testid="spare-part-unit"
                      className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                {UNITS.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
              </select>
            </Field>
            <Field label="Storage location">
              <input type="text" value={form.storage_location}
                     onChange={(e) => setF("storage_location", e.target.value)}
                     data-testid="spare-part-location"
                     placeholder="e.g. Rack 3 · Shelf B"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </Field>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Purchase price (₹)">
              <input type="number" value={form.purchase_price}
                     onChange={(e) => setF("purchase_price", e.target.value)}
                     data-testid="spare-part-purchase-price"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </Field>
            <Field label="Selling price (₹)">
              <input type="number" value={form.selling_price}
                     onChange={(e) => setF("selling_price", e.target.value)}
                     data-testid="spare-part-selling-price"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </Field>
            <Field label="GST %">
              <input type="number" step="0.5" value={form.gst_rate}
                     onChange={(e) => setF("gst_rate", e.target.value)}
                     data-testid="spare-part-gst"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </Field>
            <Field label="Reorder level">
              <input type="number" value={form.reorder_level}
                     onChange={(e) => setF("reorder_level", e.target.value)}
                     data-testid="spare-part-reorder"
                     className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </Field>
          </div>

          {/* More details */}
          <div className="rounded-xl border border-[#E4E7EC]">
            <button type="button"
                    onClick={() => setMoreOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F7F9FC]"
                    data-testid="spare-part-more-toggle">
              <div>
                <div className="text-[13px] font-bold text-[#1d3557]">More Details</div>
                <div className="text-[11px] text-[#667085]">Vehicle applicability, HSN, preferred vendor, notes.</div>
              </div>
              {moreOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>
            {moreOpen && (
              <div className="border-t border-[#F1FAEE] p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Vehicle make">
                    <input type="text" value={form.vehicle_make}
                           onChange={(e) => setF("vehicle_make", e.target.value)}
                           className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
                  </Field>
                  <Field label="Vehicle model">
                    <input type="text" value={form.vehicle_model}
                           onChange={(e) => setF("vehicle_model", e.target.value)}
                           className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
                  </Field>
                  <Field label="Variant">
                    <input type="text" value={form.variant}
                           onChange={(e) => setF("variant", e.target.value)}
                           className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
                  </Field>
                  <Field label="Alternate part number">
                    <input type="text" value={form.alternate_part_number}
                           onChange={(e) => setF("alternate_part_number", e.target.value)}
                           className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
                  </Field>
                  <Field label="HSN code">
                    <input type="text" value={form.hsn_code}
                           onChange={(e) => setF("hsn_code", e.target.value)}
                           className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
                  </Field>
                  <Field label="Max stock level">
                    <input type="number" value={form.max_stock_level || ""}
                           onChange={(e) => setF("max_stock_level", e.target.value)}
                           className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
                  </Field>
                </div>
                <Field label="Preferred vendor">
                  <select value={form.preferred_vendor_id || ""}
                          onChange={(e) => setF("preferred_vendor_id", e.target.value || null)}
                          className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                    <option value="">—</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </Field>
                <Field label="Notes">
                  <textarea rows={2} value={form.notes}
                            onChange={(e) => setF("notes", e.target.value)}
                            className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
                </Field>
                <label className="inline-flex items-center gap-2 text-[13px] text-[#1d3557]">
                  <input type="checkbox" checked={form.active}
                         onChange={(e) => setF("active", e.target.checked)}
                         data-testid="spare-part-active"
                         className="w-4 h-4 accent-[#e63946]" />
                  Part is active
                </label>
              </div>
            )}
          </div>
        </div>

        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !form.name.trim()}
                  data-testid="spare-part-save-btn"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Saving…" : (isEdit ? "Save changes" : "Create part")}
          </button>
        </footer>
      </div>
    </div>
  );
}

function defaultForm(initial) {
  return {
    name: initial?.name || "",
    part_number: initial?.part_number || "",
    brand: initial?.brand || "",
    category: initial?.category || "",
    unit: initial?.unit || "nos",
    purchase_price: initial?.purchase_price || 0,
    selling_price: initial?.selling_price || 0,
    gst_rate: initial?.gst_rate ?? 18,
    reorder_level: initial?.reorder_level || 0,
    max_stock_level: initial?.max_stock_level || null,
    storage_location: initial?.storage_location || "",
    vehicle_make: initial?.vehicle_make || "",
    vehicle_model: initial?.vehicle_model || "",
    variant: initial?.variant || "",
    alternate_part_number: initial?.alternate_part_number || "",
    hsn_code: initial?.hsn_code || "",
    preferred_vendor_id: initial?.preferred_vendor_id || null,
    notes: initial?.notes || "",
    active: initial?.active !== false,
  };
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
